import React, {FC} from 'react';
import styled from 'styled-components';

import CrmSyncUpsellImageSrc from '../../../../../../../../../src/assets/images/crmSyncUpsell.svg';
import {useLinkNavigation} from '../../../../../../../../../src/components/common/navigation/useLinkNavigation';
import {UpsellCard} from '../../../../../../../../../src/components/common/upsellCard/upsellCard';
import {Pill} from '../../../../../../../../../src/components/pill/pill';
import {CrmSyncSourcesEnum} from '../../../../../../../../../src/models/api/crmSyncModel';
import {buildSettingsBillingUrl} from '../../../../../../../../../src/models/url/settingsUrlHelpers';
import {openInBrowser} from '../../../../../../../../../srcCommon/browser/openInBrowser';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../../../shared/ui/src/styles/commonStyles';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';

/*
 * Constants.
 */

const mainHelpCenterLink = 'https://help.front.com';

/*
 * Props.
 */

export interface CrmSyncsUpsellCardProps {
  isAdmin: boolean;
  source: CrmSyncSourcesEnum;
}

/*
 * Styles.
 */

const StyledUpsellWrapperDiv = styled.div`
  width: 600px;
  margin: 15px 0 24px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
  margin-bottom: -2px;
`;

/*
 * Component.
 */

export const CrmSyncsUpsellCard: FC<CrmSyncsUpsellCardProps> = (props) => {
  const {isAdmin, source} = props;
  const {navigateToLink} = useLinkNavigation();
  const {displayName, card, account} = useCrmPageConfig(source);
  const helpCenterLink = account?.helpCenterLink || card?.helpCenterLink || mainHelpCenterLink;
  return (
    <StyledUpsellWrapperDiv>
      <UpsellCard
        title={
          card && account ? (
            <Trans id="crm-syncs-page-upsell-title-card-and-account">
              Instantly sync accounts and contacts from {displayName}
            </Trans>
          ) : (
            <Trans id="crm-syncs-page-upsell-title-account">Instantly sync accounts from {displayName}</Trans>
          )
        }
        pill={
          <Pill size={VisualSizesEnum.MEDIUM}>
            <Trans id="crm-syncs-page-upsell-pill">AVAILABLE WITH GROWTH PLAN</Trans>
          </Pill>
        }
        imageUrl={CrmSyncUpsellImageSrc}
        buttons={[
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => navigateToLink(buildSettingsBillingUrl())}
            isDisabled={!isAdmin}
          >
            <Trans id="crm-syncs-page-upsell-button">Upgrade</Trans>
          </Button>,
          <Button variant={ButtonVariantsEnum.LINK} onClick={() => openInBrowser(helpCenterLink)}>
            <StyledButtonContentDiv>
              <Trans id="crm-syncs-page-upsell-button-learn-more">Learn More</Trans>
              <StyledIcon name="externalLinkSmall" />
            </StyledButtonContentDiv>
          </Button>,
        ]}
        limitContentWidth={false}
        shouldIgnoreImagePadding
        helperText={
          isAdmin ? undefined : (
            <Trans id="crm-syncs-page-upsell-helper-text">Ask your admin about upgrading</Trans>
          )
        }
      >
        {card && account ? (
          <Trans id="crm-syncs-page-upsell-body-card-and-account">
            Sync in minutes, and then leverage {displayName} account and contact data in Front&apos;s inbox,
            rules, and analytics.
          </Trans>
        ) : (
          <Trans id="crm-syncs-page-upsell-body-account">
            Sync in minutes, and then leverage {displayName} account data in Front&apos;s inbox, rules, and
            analytics.
          </Trans>
        )}
      </UpsellCard>
    </StyledUpsellWrapperDiv>
  );
};
